import { MouseEvent, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";
import Paper, { PaperProps } from "@mui/material/Paper";

import { ChevronDownIcon, colors } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import ListChoice from "components/DesignComponents/ListChoice";
import Menu from "components/DesignComponents/Menu";
import { MenuAnchorEl } from "shared/types/types";

import { CompanyFilterContext } from "../../CompanyFilterState";
import { ALL_COMPANY_TYPE_OPTIONS, ROOT_FILTER } from "../../constants";
import { RootCompanyTypeOption } from "../../types/types";

const CustomPaper = (props: PaperProps) => {
    const { blue700, gray200 } = colors;

    const { isLoading, rootCompanyType, setRootCompanyType } =
        useContext(CompanyFilterContext);

    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

    const openMenu = (e: MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(e.currentTarget);

    const closeMenu = () => setAnchorEl(null);

    const handleOptionSelect = (option: RootCompanyTypeOption) => {
        setRootCompanyType(option);
        localStorage.setItem(ROOT_FILTER, option);
        closeMenu();
    };

    return (
        <Paper {...props}>
            <div
                css={css({
                    position: "relative",
                    minHeight: "60px",

                    "& .MuiAutocomplete-listbox": {
                        opacity: isLoading ? "0.4" : "1",
                        pointerEvents: isLoading ? "none" : "auto",
                        transition: "opacity 300ms ease-out"
                    }
                })}
            >
                {props.children}

                <div
                    aria-hidden={!isLoading}
                    css={css({
                        position: "absolute",
                        top: "0",
                        bottom: "0",
                        left: "0",
                        right: "0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        opacity: isLoading ? "1" : "0",
                        pointerEvents: "none"
                    })}
                >
                    <CircularProgress
                        css={css({
                            color: blue700
                        })}
                    />
                </div>
            </div>

            <div
                onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                css={css({
                    borderTop: `1px solid ${gray200}`,
                    padding: "11px 16px 12px 16px",
                    textAlign: "right"
                })}
            >
                <Button
                    variant={BUTTON_VARIANTS.IconRight}
                    size={BUTTON_SIZES.Small}
                    color={BUTTON_COLORS.White}
                    icon={<ChevronDownIcon />}
                    onClick={openMenu}
                >
                    {t("General##company type")}:{" "}
                    {t(`Dialog##company type##${rootCompanyType}`)}
                </Button>

                <Menu
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={closeMenu}
                    autoFocus={false}
                    disableAutoFocus
                    disableEnforceFocus
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: "216px"
                            }
                        }
                    }}
                >
                    {ALL_COMPANY_TYPE_OPTIONS.map(option => (
                        <ListChoice
                            key={option}
                            title={t(`Dialog##company type##${option}`)}
                            selected={rootCompanyType === option}
                            onClick={() => handleOptionSelect(option)}
                        />
                    ))}
                </Menu>
            </div>
        </Paper>
    );
};

export default CustomPaper;
